import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  Alert,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AddTaskIcon from '@mui/icons-material/AddTask';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useBrand } from '../contexts/BrandContext';
import { useAuth } from '../contexts/AuthContext';
import { auditsApi } from '../services/audits';
import { tasksApi } from '../services/tasks';
import type { Audit, EarnedMediaAuditSummary, AgentStatus, AuthorityGap } from '@traaker/shared';
import { EarnedMediaV2Tabs } from '../components/earned-media/EarnedMediaV2Tabs';

const STATUS_COLORS: Record<string, 'default' | 'info' | 'success' | 'error'> = {
  pending: 'default',
  running: 'info',
  completed: 'success',
  failed: 'error',
};

const STATUS_LABELS: Record<string, string> = {
  pending: 'En attente',
  running: 'En cours',
  completed: 'Termine',
  failed: 'Echoue',
};

const AGENT_STATUS_COLORS: Record<AgentStatus, string> = {
  pending: '#A0A4AE',
  running: '#F59E0B',
  completed: '#10B981',
  failed: '#EF4444',
};

const SENTIMENT_COLORS: Record<string, string> = {
  positive: '#00DDB3',
  neutral: '#8B9DC3',
  negative: '#FF4444',
};

const SOURCE_TYPE_LABELS: Record<string, string> = {
  press: 'Presse',
  academic: 'Academique',
  industry: 'Industrie',
  review: 'Avis',
  wikipedia: 'Wikipedia',
  forum: 'Forum',
};

const MEDIA_TYPE_LABELS: Record<string, string> = {
  earned: 'Earned',
  owned: 'Owned',
  social: 'Social',
};

const MEDIA_TYPE_COLORS: Record<string, string> = {
  earned: '#00DDB3',
  owned: '#8B9DC3',
  social: '#FF6B35',
};

/**
 * V2 discriminator — true when the summary was produced by the
 * orchestrator (Phase E2). Any one of these fields signals v2; absence of
 * all of them = legacy v1 audit, which renders only the v1 sections below.
 */
function _isV2Summary(s: EarnedMediaAuditSummary): boolean {
  return Boolean(
    s.recommendations_categorized ||
      s.engine_influence ||
      (s.missing_authorities && s.missing_authorities.length > 0) ||
      (s.conversations && s.conversations.length > 0),
  );
}

function ScoreBadge({ score, suffix = '/100' }: { score: number; suffix?: string }) {
  const color = score >= 70 ? '#00DDB3' : score >= 40 ? '#FF6B35' : '#FF4444';
  return (
    <Typography fontWeight={700} sx={{ color }}>
      {score}{suffix}
    </Typography>
  );
}

export default function EarnedMedia() {
  const { activeBrand } = useBrand();
  const [audits, setAudits] = useState<Audit[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [selectedAudit, setSelectedAudit] = useState<Audit | null>(null);

  const loadAudits = async () => {
    try {
      const data = await auditsApi.list('earned_media', activeBrand?.brandId);
      setAudits(data);
    } catch {
      setError('Erreur lors du chargement des audits');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    setSelectedAudit(null);
    loadAudits();
  }, [activeBrand?.brandId]);

  const handleCreate = async () => {
    setCreating(true);
    setError('');
    try {
      await auditsApi.create('earned_media', {}, activeBrand?.brandId);
      setDialogOpen(false);
      await loadAudits();
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la creation');
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Detail view
  if (selectedAudit) {
    return (
      <EarnedMediaDetail
        audit={selectedAudit}
        onBack={() => setSelectedAudit(null)}
        onRefresh={async () => {
          const updated = await auditsApi.getById(selectedAudit.auditId);
          if (updated) setSelectedAudit(updated);
        }}
      />
    );
  }

  // List view
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight={700}>
          Earned Media
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
          Nouvel Audit
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {audits.length === 0 ? (
        <Card>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              Aucun audit Earned Media
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Analysez la presence de {activeBrand?.name || 'votre marque'} dans les sources tierces
              autoritaires — presse, Wikipedia, avis d'experts — les contenus que les moteurs IA
              citent le plus.
            </Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
              Lancer un audit
            </Button>
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Card}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Marque</TableCell>
                <TableCell>Statut</TableCell>
                <TableCell>Agent</TableCell>
                <TableCell>Score</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {audits.map((audit) => {
                const agentStatus = audit.agentStatuses?.earned_media || 'pending';
                return (
                  <TableRow
                    key={audit.auditId}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => setSelectedAudit(audit)}
                  >
                    <TableCell>
                      {new Date(audit.createdAt).toLocaleDateString('fr-FR', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </TableCell>
                    <TableCell>{activeBrand?.name || '—'}</TableCell>
                    <TableCell>
                      <Chip
                        label={STATUS_LABELS[audit.status] || audit.status}
                        color={STATUS_COLORS[audit.status] || 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title={`Earned Media: ${STATUS_LABELS[agentStatus] || agentStatus}`}>
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            bgcolor: AGENT_STATUS_COLORS[agentStatus] || '#555',
                          }}
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Typography fontWeight={600}>
                        {audit.earnedMediaSummary?.overall_earned_media_score ?? '—'}
                        {audit.earnedMediaSummary?.overall_earned_media_score !== undefined && '/100'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nouvel Audit Earned Media</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Typography variant="body1">
              Analyser les medias earned de <strong>{activeBrand?.name || '—'}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              L'audit recherchera la presence de votre marque dans les sources tierces
              autoritaires : presse, Wikipedia, avis d'experts, forums. Ce sont les
              sources que les moteurs IA citent et recommandent le plus.
            </Typography>
            {!activeBrand?.strategy && (
              <Alert severity="warning">
                Configurez la Brand Knowledge pour de meilleurs resultats. Sans strategie,
                l'agent utilisera uniquement le nom et le domaine de la marque.
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)}>Annuler</Button>
          <Button variant="contained" onClick={handleCreate} disabled={creating}>
            {creating ? <CircularProgress size={20} /> : "Lancer l'audit"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ── Detail view ──

function EarnedMediaRecItem({ rec, auditId, brandId }: { rec: string; auditId: string; brandId?: string }) {
  const { tenant } = useAuth();
  const [adding, setAdding] = useState(false);
  const [added, setAdded] = useState(false);
  const tasksEnabled = !!tenant?.settings?.featureFlags?.tasksEnabled;

  const handleAdd = async () => {
    if (added || adding) return;
    setAdding(true);
    try {
      await tasksApi.create({
        title: rec.slice(0, 120),
        description: rec,
        category: 'earned_media',
        sourceType: 'audit_earned_media',
        auditId,
        brandId,
      });
      setAdded(true);
    } catch { /* ignore */ } finally {
      setAdding(false);
    }
  };

  return (
    <ListItem disableGutters>
      <ListItemIcon sx={{ minWidth: 32 }}>
        <LightbulbIcon fontSize="small" sx={{ color: '#FF6B35' }} />
      </ListItemIcon>
      <ListItemText primary={rec} primaryTypographyProps={{ variant: 'body2' }} />
      {tasksEnabled && (
        <Tooltip title={added ? 'Ajoute aux taches' : 'Ajouter aux taches'}>
          <IconButton size="small" onClick={handleAdd} disabled={adding} sx={{ ml: 0.5 }}>
            {adding ? (
              <CircularProgress size={14} />
            ) : added ? (
              <CheckCircleOutlineIcon fontSize="small" sx={{ color: '#00DDB3' }} />
            ) : (
              <AddTaskIcon fontSize="small" sx={{ color: '#7C3AED' }} />
            )}
          </IconButton>
        </Tooltip>
      )}
    </ListItem>
  );
}

function EarnedMediaDetail({
  audit: initialAudit,
  onBack,
  onRefresh,
}: {
  audit: Audit;
  onBack: () => void;
  onRefresh: () => Promise<void>;
}) {
  const { activeBrand } = useBrand();
  const [audit, setAudit] = useState(initialAudit);
  const [summary, setSummary] = useState<EarnedMediaAuditSummary | null>(
    initialAudit.earnedMediaSummary || null,
  );
  const [sources, setSources] = useState<any[]>([]);
  const [inaccuracies, setInaccuracies] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [auditData, summaryData, resultsData] = await Promise.all([
        auditsApi.getById(audit.auditId),
        auditsApi.getSummary(audit.auditId).catch(() => null),
        auditsApi.getResults(audit.auditId, 'earned_media').catch(() => []),
      ]);
      if (auditData) {
        setAudit(auditData);
        setSummary(auditData.earnedMediaSummary || (summaryData as EarnedMediaAuditSummary | null) || null);
      }
      // Separate sources from inaccuracies based on SK prefix
      const srcList: any[] = [];
      const inaccList: any[] = [];
      for (const r of resultsData) {
        if (r.SK?.includes('INACCURACY')) {
          inaccList.push(r);
        } else {
          srcList.push(r);
        }
      }
      setSources(srcList);
      setInaccuracies(inaccList);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [audit.auditId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadData();
    await onRefresh();
    setRefreshing(false);
  };

  const agentStatus = audit.agentStatuses?.earned_media || 'pending';
  const isV2 = !!summary && _isV2Summary(summary);

  return (
    <Box>
      <Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ mb: 2 }}>
        Retour
      </Button>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Typography variant="h4" fontWeight={700}>
          Earned Media — {new Date(audit.createdAt).toLocaleDateString('fr-FR')}
        </Typography>
        <Chip
          label={STATUS_LABELS[audit.status] || audit.status}
          color={STATUS_COLORS[audit.status] || 'default'}
        />
        <Chip
          label={`Agent: ${STATUS_LABELS[agentStatus] || agentStatus}`}
          size="small"
          sx={{
            bgcolor: `${AGENT_STATUS_COLORS[agentStatus]}20`,
            color: AGENT_STATUS_COLORS[agentStatus],
            fontWeight: 600,
          }}
        />
        <Tooltip title="Rafraichir">
          <IconButton onClick={handleRefresh} disabled={refreshing}>
            {refreshing ? <CircularProgress size={20} /> : <RefreshIcon />}
          </IconButton>
        </Tooltip>
      </Box>

      {audit.status === 'running' && <LinearProgress sx={{ mb: 3, borderRadius: 1 }} />}

      {audit.status === 'pending' && (
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress sx={{ mb: 2 }} />
            <Typography color="text.secondary">
              Audit en attente de traitement...
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Summary cards — global header. Always rendered first when present. */}
      {summary && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 6, sm: 4, md: 2 }}>
            <SummaryCard title="Score Global" value={`${summary.overall_earned_media_score}`} suffix="/100" color="#7C3AED" />
          </Grid>
          <Grid size={{ xs: 6, sm: 4, md: 2 }}>
            <SummaryCard title="Wikipedia" value={`${summary.wikipedia_quality_score}`} suffix="/100" color="#8B5CF6" />
          </Grid>
          <Grid size={{ xs: 6, sm: 4, md: 2 }}>
            <SummaryCard title="E-E-A-T" value={`${summary.eeat_score}`} suffix="/100" color="#00DDB3" />
          </Grid>
          <Grid size={{ xs: 6, sm: 4, md: 2 }}>
            <SummaryCard title="Precision" value={`${summary.accuracy_score}`} suffix="/100" color="#FF6B35" />
          </Grid>
          <Grid size={{ xs: 6, sm: 4, md: 2 }}>
            <SummaryCard title="Sources" value={`${summary.authority_mention_count}`} suffix="" color="#E91E63" />
          </Grid>
          <Grid size={{ xs: 6, sm: 4, md: 2 }}>
            <SummaryCard title="Inexactitudes" value={`${summary.inaccuracies_count}`} suffix="" color="#FF4444" />
          </Grid>
        </Grid>
      )}

      {/* V2 tabs (orchestrator-produced summaries only). Rendered AFTER the
          score strip so the global header is always at the top of the page.
          v1 sections below are suppressed when v2 is present (their content
          lives in the tabs) to avoid duplication. */}
      {summary && _isV2Summary(summary) && (
        <EarnedMediaV2Tabs
          summary={summary}
          sources={sources}
          inaccuracies={inaccuracies}
          onCreateTask={async (title, severity) => {
            try {
              await tasksApi.create({
                title,
                priority: severity === 'high' ? 'high' : severity === 'low' ? 'low' : 'medium',
                source: { type: 'audit_earned_media', auditId: audit.auditId },
                ...(activeBrand?.brandId ? { brandId: activeBrand.brandId } : {}),
              } as any);
            } catch {
              // best effort
            }
          }}
        />
      )}

      {/* Coverage gaps — v1-only; v2 surfaces this in the "Médias à conquérir" tab. */}
      {!isV2 && (summary?.coverage_gap_details?.length || summary?.coverage_gaps?.length) ? (
        <Card sx={{ mb: 3, bgcolor: 'rgba(255, 107, 53, 0.04)' }}>
          <CardContent>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <WarningAmberIcon sx={{ color: '#FF6B35' }} />
              Couverture manquante ({summary.coverage_gap_details?.length || summary.coverage_gaps?.length})
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
              Sources d'autorite attendues mais sans mention trouvee.
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {((summary.coverage_gap_details
                ?? summary.coverage_gaps?.map((d) => ({ domain: d } as AuthorityGap))
                ?? []) as AuthorityGap[]
              ).map((gap) => (
                <Box key={gap.domain} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <Chip label={gap.domain} size="small" variant="outlined" sx={{ mt: 0.25 }} />
                  {gap.category && (
                    <Chip
                      label={String(gap.category).replace(/_/g, ' ')}
                      size="small"
                      sx={{ mt: 0.25, bgcolor: 'rgba(124, 58, 237, 0.10)', color: '#7C3AED' }}
                    />
                  )}
                  {gap.rationale && (
                    <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                      {gap.rationale}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>
      ) : null}

      {/* Recommendations — v1-only flat list; v2 splits these into per-tab sections. */}
      {!isV2 && summary?.recommendations && summary.recommendations.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
              Recommandations
            </Typography>
            <List dense disablePadding>
              {summary.recommendations.map((rec, i) => (
                <EarnedMediaRecItem
                  key={i}
                  rec={rec}
                  auditId={audit.auditId}
                  brandId={activeBrand?.brandId}
                />
              ))}
            </List>
          </CardContent>
        </Card>
      )}

      {/* Sources table — v1-only. v2 renders sources inside the "Médias acquis" tab
          with inaccuracy join + expandable details, so this would duplicate.
          The whole block is gated on !isV2 — including the empty-state "Aucune
          source trouvée" branch — so v2 audits don't render a misleading empty
          card on every tab. */}
      {isV2 ? null : loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={24} />
        </Box>
      ) : sources.length > 0 ? (
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ pb: 0 }}>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
              Sources trouvees ({sources.length})
            </Typography>
          </CardContent>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Source</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Media</TableCell>
                  <TableCell>Sentiment</TableCell>
                  <TableCell>Pertinence</TableCell>
                  <TableCell>Precise</TableCell>
                  <TableCell width={40} />
                </TableRow>
              </TableHead>
              <TableBody>
                {sources.map((s: any, i: number) => (
                  <TableRow key={s.SK || i}>
                    <TableCell sx={{ maxWidth: 250 }}>
                      <Typography variant="body2" fontWeight={600} noWrap>
                        {s.sourceName}
                      </Typography>
                      {s.excerpt && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }} noWrap>
                          {s.excerpt}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={SOURCE_TYPE_LABELS[s.sourceType] || s.sourceType}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={MEDIA_TYPE_LABELS[s.mediaType] || s.mediaType}
                        size="small"
                        sx={{
                          bgcolor: `${MEDIA_TYPE_COLORS[s.mediaType] || '#888'}20`,
                          color: MEDIA_TYPE_COLORS[s.mediaType] || '#888',
                          fontWeight: 600,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={s.sentiment}
                        size="small"
                        sx={{
                          bgcolor: `${SENTIMENT_COLORS[s.sentiment] || '#888'}20`,
                          color: SENTIMENT_COLORS[s.sentiment] || '#888',
                          fontWeight: 600,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <ScoreBadge score={s.relevanceScore ?? 0} suffix="" />
                    </TableCell>
                    <TableCell>
                      {s.brandAccurate ? (
                        <Chip label="Oui" size="small" color="success" variant="outlined" />
                      ) : (
                        <Chip label="Non" size="small" color="error" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell>
                      {s.sourceUrl && (
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            window.open(s.sourceUrl, '_blank');
                          }}
                        >
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      ) : audit.status === 'completed' ? (
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ textAlign: 'center', py: 3 }}>
            <Typography color="text.secondary">Aucune source trouvee.</Typography>
          </CardContent>
        </Card>
      ) : null}

      {/* Inaccuracies — v1-only. v2 renders these inline inside acquired-domain rows
          + as full cards in the "Précision" tab. */}
      {!isV2 && inaccuracies.length > 0 && (
        <Card>
          <CardContent>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <WarningAmberIcon sx={{ color: '#FF6B35' }} />
              Inexactitudes detectees ({inaccuracies.length})
            </Typography>
            {inaccuracies.map((item: any, i: number) => (
              <InaccuracyCard
                key={item.SK || i}
                item={item}
                auditId={audit.auditId}
                brandId={activeBrand?.brandId}
              />
            ))}
          </CardContent>
        </Card>
      )}
    </Box>
  );
}

function InaccuracyCard({ item, auditId, brandId }: { item: any; auditId: string; brandId?: string }) {
  const { tenant } = useAuth();
  const [adding, setAdding] = useState(false);
  const [added, setAdded] = useState(false);
  const tasksEnabled = !!tenant?.settings?.featureFlags?.tasksEnabled;

  const handleAdd = async () => {
    if (added || adding) return;
    setAdding(true);
    try {
      await tasksApi.create({
        title: `Inexactitude — ${item.sourceName}`.slice(0, 120),
        description: `Affirmation : ${item.claim}\nRealite : ${item.reality}`,
        category: 'earned_media',
        sourceType: 'audit_earned_media',
        auditId,
        brandId,
        sourcePageUrl: item.sourceUrl,
      });
      setAdded(true);
    } catch { /* ignore */ } finally {
      setAdding(false);
    }
  };

  return (
    <Card variant="outlined" sx={{ mb: 1.5, bgcolor: 'rgba(255, 107, 53, 0.04)' }}>
      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
          <Typography variant="body2" fontWeight={600}>
            {item.sourceName}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Chip
              label={item.severity === 'critical' ? 'Critique' : 'Mineur'}
              size="small"
              color={item.severity === 'critical' ? 'error' : 'warning'}
            />
            {tasksEnabled && (
              <Tooltip title={added ? 'Ajoute aux taches' : 'Ajouter aux taches'}>
                <IconButton size="small" onClick={handleAdd} disabled={adding}>
                  {adding ? (
                    <CircularProgress size={14} />
                  ) : added ? (
                    <CheckCircleOutlineIcon fontSize="small" sx={{ color: '#00DDB3' }} />
                  ) : (
                    <AddTaskIcon fontSize="small" sx={{ color: '#7C3AED' }} />
                  )}
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>
        <Typography variant="body2" color="text.secondary">
          <strong>Affirmation :</strong> {item.claim}
        </Typography>
        <Typography variant="body2" sx={{ color: '#00DDB3' }}>
          <strong>Realite :</strong> {item.reality}
        </Typography>
      </CardContent>
    </Card>
  );
}

function SummaryCard({ title, value, suffix, color }: {
  title: string;
  value: string;
  suffix: string;
  color: string;
}) {
  return (
    <Card>
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Typography variant="caption" color="text.secondary">{title}</Typography>
        <Typography variant="h5" fontWeight={700} sx={{ color }}>
          {value}{suffix}
        </Typography>
      </CardContent>
    </Card>
  );
}
